package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderTop
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Progress data for each round.
 */
data class RoundProgress(
    val round: RoundName,
    val totalMatches: Int,
    val pickedMatches: Int
) {
    val isComplete: Boolean get() = pickedMatches == totalMatches
    val isPartial: Boolean get() = pickedMatches > 0 && pickedMatches < totalMatches
    val isEmpty: Boolean get() = pickedMatches == 0
    val percentage: Double get() = if (totalMatches > 0) (pickedMatches.toDouble() / totalMatches * 100) else 0.0
}

/**
 * Calculate progress for each round.
 */
fun calculateRoundProgress(
    rounds: List<BracketRoundEntity>,
    picks: Map<String, String>
): List<RoundProgress> {
    return rounds.map { round ->
        val pickedCount = round.matches.count { match ->
            picks.containsKey(match.player1.id)
        }
        RoundProgress(
            round = round.name,
            totalMatches = round.matches.size,
            pickedMatches = pickedCount
        )
    }
}

/**
 * Enhanced progress bar showing completion status by round.
 */
@Composable
fun BracketProgressBar(
    rounds: List<BracketRoundEntity>,
    picks: Map<String, String>,
    maxPoints: Int,
    showRoundDetails: Boolean = true
) {
    val roundProgress = remember(rounds, picks) {
        calculateRoundProgress(rounds, picks)
    }

    val totalMatches = roundProgress.sumOf { it.totalMatches }
    val totalPicked = roundProgress.sumOf { it.pickedMatches }
    val overallPercent = if (totalMatches > 0) (totalPicked.toDouble() / totalMatches * 100) else 0.0

    Div({
        classes("bracket-progress-bar")
        style {
            backgroundColor(Colors.Surface)
            padding(16.px)
            borderRadius(8.px)
            property("box-shadow", "0 1px 3px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Header row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(12.px)
            }
        }) {
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                }
            }) {
                Text("Bracket Progress")
            }

            Span({
                style {
                    fontSize(14.px)
                    fontWeight(700)
                    color(Colors.Primary)
                }
            }) {
                Text("$totalPicked / $totalMatches picks (${overallPercent.toInt()}%)")
            }
        }

        // Main progress bar
        Div({
            style {
                height(8.px)
                backgroundColor(Colors.Gray200)
                borderRadius(4.px)
                overflow("hidden")
                marginBottom(if (showRoundDetails) 16.px else 0.px)
            }
        }) {
            Div({
                style {
                    height(100.percent)
                    width(overallPercent.percent)
                    backgroundColor(
                        when {
                            overallPercent >= 100 -> Colors.Success
                            overallPercent >= 50 -> Colors.Primary
                            else -> Colors.Accent
                        }
                    )
                    property("transition", "width 0.3s ease")
                }
            })
        }

        // Round-by-round breakdown
        if (showRoundDetails) {
            Div({
                classes("round-badges")
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                roundProgress.forEach { progress ->
                    RoundProgressBadge(progress)
                }
            }

            // Potential points
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    paddingTop(12.px)
                    borderTop(1.px, LineStyle.Solid, Colors.BorderLight)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Potential Points")
                }

                Span({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(Colors.Secondary)
                    }
                }) {
                    // Calculate potential points based on current picks
                    val potentialPoints = calculatePotentialPoints(roundProgress)
                    Text("$potentialPoints / $maxPoints max")
                }
            }
        }
    }
}

@Composable
private fun RoundProgressBadge(progress: RoundProgress) {
    val bgColor = when {
        progress.isComplete -> Colors.SuccessLight
        progress.isPartial -> Colors.WarningLight
        else -> Colors.Gray100
    }

    val textColor = when {
        progress.isComplete -> Colors.SuccessDark
        progress.isPartial -> Colors.WarningDark
        else -> Colors.TextSecondary
    }

    val indicatorColor = when {
        progress.isComplete -> Colors.Success
        progress.isPartial -> Colors.Warning
        else -> Colors.Gray400
    }

    Div({
        classes("round-badge")
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(4.px, 10.px)
            backgroundColor(bgColor)
            borderRadius(16.px)
        }
    }) {
        // Status indicator dot
        Div({
            style {
                property("width", "8px")
                property("height", "8px")
                borderRadius(50.percent)
                backgroundColor(indicatorColor)
            }
        })

        // Round name
        Span({
            style {
                fontSize(11.px)
                fontWeight(500)
                color(textColor)
            }
        }) {
            Text(progress.round.displayName.take(3)) // Abbreviated
        }

        // Count
        Span({
            style {
                fontSize(10.px)
                color(textColor)
            }
        }) {
            Text("${progress.pickedMatches}/${progress.totalMatches}")
        }
    }
}

private fun calculatePotentialPoints(roundProgress: List<RoundProgress>): Int {
    return roundProgress.sumOf { progress ->
        progress.pickedMatches * progress.round.basePoints
    }
}

/**
 * Compact progress indicator for header.
 */
@Composable
fun CompactProgressIndicator(
    totalPicks: Int,
    totalMatches: Int,
    percentage: Double
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        // Mini progress bar
        Div({
            style {
                width(80.px)
                height(6.px)
                backgroundColor(Colors.Gray300)
                borderRadius(3.px)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    height(100.percent)
                    width(percentage.percent)
                    backgroundColor(
                        when {
                            percentage >= 100 -> Colors.Success
                            percentage >= 50 -> Colors.Primary
                            else -> Colors.Accent
                        }
                    )
                }
            })
        }

        // Text
        Span({
            style {
                fontSize(12.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("$totalPicks/$totalMatches")
        }
    }
}

/**
 * Round navigation with progress indicators.
 * Alternative to the tree view for mobile or preference.
 */
@Composable
fun RoundNavigatorWithProgress(
    rounds: List<BracketRoundEntity>,
    picks: Map<String, String>,
    selectedRound: RoundName,
    onSelectRound: (RoundName) -> Unit
) {
    val roundProgress = remember(rounds, picks) {
        calculateRoundProgress(rounds, picks)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            overflowX("auto")
            padding(8.px, 0.px)
            property("-webkit-overflow-scrolling", "touch")
        }
    }) {
        roundProgress.forEachIndexed { index, progress ->
            val isSelected = progress.round == selectedRound

            // Round tab
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    padding(8.px, 12.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    backgroundColor(if (isSelected) Colors.Primary else Colors.Surface)
                    property("transition", "all 0.2s")
                    property("white-space", "nowrap")
                }
                onClick { onSelectRound(progress.round) }
            }) {
                // Round name
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(if (isSelected) 600 else 400)
                        color(if (isSelected) Colors.White else Colors.TextPrimary)
                    }
                }) {
                    Text(progress.round.displayName)
                }

                // Progress indicator
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    // Mini dots
                    val completeDots = progress.pickedMatches
                    val emptyDots = progress.totalMatches - progress.pickedMatches

                    repeat(minOf(completeDots, 4)) {
                        Div({
                            style {
                                property("width", "6px")
                                property("height", "6px")
                                borderRadius(50.percent)
                                backgroundColor(if (isSelected) Colors.White else Colors.Success)
                            }
                        })
                    }

                    repeat(minOf(emptyDots, 4)) {
                        Div({
                            style {
                                property("width", "6px")
                                property("height", "6px")
                                borderRadius(50.percent)
                                backgroundColor(if (isSelected) rgba(255, 255, 255, 0.4) else Colors.Gray300)
                            }
                        })
                    }

                    // Show count if more than 4 matches
                    if (progress.totalMatches > 4) {
                        Span({
                            style {
                                fontSize(9.px)
                                color(if (isSelected) Colors.White else Colors.TextSecondary)
                            }
                        }) {
                            Text("${progress.pickedMatches}/${progress.totalMatches}")
                        }
                    }
                }
            }

            // Arrow between rounds (except after last)
            if (index < roundProgress.size - 1) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.Gray400)
                        padding(0.px, 4.px)
                    }
                }) {
                    Text("→")
                }
            }
        }
    }
}
